import { useContext } from "react";
import ThemeContext from "../../lib/theme-context";

const EventBadge = ({ event, onClick, isDraggable, onDragStart }) => {
  const theme = useContext(ThemeContext);
  const isDark = theme[0] === "dark";
  const isDesktop = typeof window !== "undefined" && window.innerWidth >= 640;

  const handleDragStart = (e) => {
    if (!isDraggable) {
      e.preventDefault();
      return;
    }
    if (onDragStart) {
      onDragStart(e, event);
    }
  };

  const getTypeColor = (type) => {
    if (type === "Main") return "#26a65d";
    if (type === "Additional") return "#ED9E0E";
    if (type === "Potential") return "#4299e1";
    if (type === "Holiday") return "#9333EA";
    return event.color;
  };

  const typeColor = event.forecastType
    ? getTypeColor(event.forecastType)
    : null;

  return (
    <div
      draggable={isDraggable}
      onDragStart={handleDragStart}
      onClick={(e) => {
        e.stopPropagation();
        // Don't allow clicking on holiday badges
        if (event.category === "holiday") return;
        if (onClick) onClick(event);
      }}
      style={{
        backgroundColor: isDark ? `${event.color}15` : `${event.color}10`,
        color:
          event.category === "holiday"
            ? isDark
              ? "rgba(252, 252, 255, 0.9)"
              : "rgba(26, 27, 30, 0.9)"
            : isDark
              ? "#FCFCFF"
              : "#1a1b1e",
        fontSize: isDesktop
          ? "clamp(9px, 1vw, 10px)"
          : "clamp(11px, 2vw, 14px)",
        fontWeight: "600",
        padding: isDesktop
          ? "clamp(2px, 0.4vw, 3px) clamp(4px, 0.6vw, 5px)"
          : "clamp(5px, 0.9vw, 7px) clamp(7px, 1.3vw, 10px)",
        borderRadius: isDesktop
          ? "clamp(2px, 0.4vw, 3px)"
          : "clamp(4px, 0.8vw, 5px)",
        marginBottom: isDesktop
          ? "clamp(1px, 0.2vw, 2px)"
          : "clamp(3px, 0.6vw, 5px)",
        cursor: onClick && event.category !== "holiday" ? "pointer" : "default",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        userSelect: "none",
        border:
          event.category === "holiday"
            ? isDark
              ? "clamp(1px, 0.15vw, 1px) solid rgba(255, 255, 255, 0.9)"
              : "clamp(1px, 0.15vw, 1px) solid rgba(0, 0, 0, 0.9)"
            : `clamp(1px, 0.15vw, 1.5px) solid ${event.color}`,
        letterSpacing: "0.3px",
        boxShadow: "none",
        display: "flex",
        alignItems: "center",
        gap: "4px",
      }}
      onMouseEnter={(e) => {
        if (onClick && event.category !== "holiday") {
          e.currentTarget.style.backgroundColor = isDark
            ? `${event.color}25`
            : `${event.color}20`;
          e.currentTarget.style.transform = "translateY(-1px)";
          e.currentTarget.style.boxShadow = isDark
            ? `0 2px 8px ${event.color}30`
            : `0 2px 8px ${event.color}25`;
        }
      }}
      onMouseLeave={(e) => {
        if (event.category !== "holiday") {
          e.currentTarget.style.backgroundColor = isDark
            ? `${event.color}15`
            : `${event.color}10`;
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "none";
        }
      }}
      title={event.title}
    >
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "flex",
          alignItems: "center",
          gap: "4px",
        }}
      >
        {(() => {
          // For holiday events, show holiday badge with currency
          if (event.category === "holiday") {
            const truncatedTitle =
              event.title.length > 23
                ? event.title.substring(0, 21) + "..."
                : event.title;

            return (
              <>
                {event.currency || "H"} • {truncatedTitle}
              </>
            );
          }
          // For forecast titles, show direction badge and forecast type badge before pair name
          if (event.title.includes("Forecast") && event.direction) {
            const parts = event.title.split(" Forecast");
            return (
              <>
                <span>{parts[0]}</span>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "1px 5px",
                    borderRadius: "4px",
                    backgroundColor:
                      event.direction === "Short"
                        ? isDark
                          ? "rgba(219, 6, 113, 0.2)"
                          : "rgba(219, 6, 113, 0.1)"
                        : event.direction?.toLowerCase() === "netural"
                          ? isDark
                            ? "rgba(237, 158, 14, 0.2)"
                            : "rgba(237, 158, 14, 0.1)"
                          : isDark
                            ? "rgba(0, 121, 207, 0.2)"
                            : "rgba(0, 121, 207, 0.1)",
                    border: `1.5px solid ${
                      event.direction === "Short"
                        ? "#db0671"
                        : event.direction?.toLowerCase() === "netural"
                          ? "#ED9E0E"
                          : "#0079cf"
                    }`,
                    color: isDark ? "#ffffff" : "#000000",
                    fontSize: "clamp(9px, 1.3vw, 10px)",
                    fontWeight: "700",
                    flexShrink: 0,
                    letterSpacing: "0.3px",
                    lineHeight: 1.2,
                  }}
                >
                  {event.direction === "Short"
                    ? "S"
                    : event.direction?.toLowerCase() === "netural"
                      ? "N"
                      : "L"}
                </span>
                <span>Forecast</span>
                {event.forecastType && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "clamp(16px, 2.5vw, 18px)",
                      height: "clamp(16px, 2.5vw, 18px)",
                      borderRadius: "50%",
                      backgroundColor: isDark
                        ? `${typeColor}30`
                        : `${typeColor}20`,
                      border: `1.5px solid ${typeColor}`,
                      color: isDark ? "#ffffff" : "#000000",
                      fontSize: "clamp(9px, 1.5vw, 10px)",
                      fontWeight: "700",
                      flexShrink: 0,
                    }}
                  >
                    {event.forecastType[0]}
                  </span>
                )}
              </>
            );
          }
          // For non-forecast items with direction, show badge before title
          if (event.direction) {
            return (
              <>
                <span>{event.title}</span>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "1px 5px",
                    borderRadius: "4px",
                    backgroundColor: isDark
                      ? `${event.color}30`
                      : `${event.color}20`,
                    border: `1.5px solid ${event.color}`,
                    color: isDark ? "#ffffff" : "#000000",
                    fontSize: "clamp(9px, 1.3vw, 10px)",
                    fontWeight: "700",
                    flexShrink: 0,
                    letterSpacing: "0.3px",
                    lineHeight: 1.2,
                  }}
                >
                  {event.direction === "Short" ? "S" : "L"}
                </span>
                {!event.isMissedTrade && <span>Trade</span>}
                {event.isMissedTrade && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "1px 5px",
                      borderRadius: "4px",
                      backgroundColor: isDark
                        ? "rgba(249, 115, 22, 0.2)"
                        : "rgba(249, 115, 22, 0.1)",
                      border: "1.5px solid #f97316",
                      color: isDark ? "#ffffff" : "#000000",
                      fontSize: "clamp(9px, 1.3vw, 10px)",
                      fontWeight: "700",
                      flexShrink: 0,
                      letterSpacing: "0.3px",
                      lineHeight: 1.2,
                    }}
                  >
                    Missed
                  </span>
                )}
              </>
            );
          }
          // Default: just show title
          return event.title;
        })()}
      </span>
    </div>
  );
};

export default EventBadge;
